import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import { currentDomain, API2_HOST, WS2_HOST, api2Prefix, ws2Prefix, apiProtocol, ws2Protocol } from '@bfe/by-dynamic-domain';
import { storage } from '@bfe/by-storage';
import { isTestnet, BYBIT_SERVICE_DOMAINS } from '@bfe/by-env';
import { detectRoute, getCalculatedRoute, removeRecords, sendRequest } from './route-finder';
import { ROUTE_CHECK_DURATION, XAPI_GRAY } from './constants';

// api2.bybit.com --> [api2-1.bybit.com, api2-2.bybit.com]
// api2-testnet.bybit.com --> [api2-1-testnet.bybit.com, api2-2-testnet.bybit.com]
var getApi2HostsByServiceDomain = function getApi2HostsByServiceDomain(domain) {
  return ["".concat(apiProtocol, "//").concat(isTestnet ? api2Prefix.replace('-', '-1-') : api2Prefix.replace('.', '-1.')).concat(domain.trim()), "".concat(apiProtocol, "//").concat(isTestnet ? api2Prefix.replace('-', '-2-') : api2Prefix.replace('.', '-2.')).concat(domain.trim())];
};

// ws2.bybit.com --> [ws2-1.bybit.com, ws2-2.bybit.com]
// ws2-testnet.bybit.com --> [ws2-1-testnet.bybit.com, ws2-2-testnet.bybit.com]
var getWs2HostsByServiceDomain = function getWs2HostsByServiceDomain(domain) {
  return ["".concat(ws2Protocol, "//").concat(isTestnet ? ws2Prefix.replace('-', '-1-') : ws2Prefix.replace('.', '-1.')).concat(domain.trim()), "".concat(ws2Protocol, "//").concat(isTestnet ? ws2Prefix.replace('-', '-2-') : ws2Prefix.replace('.', '-2.')).concat(domain.trim())];
};
// At the moment we provide 3 different routes / sub-routes
// 1. Main route
// 2. CDN1 sub route
// 3. CDN2 sub route
// Change this list will impact defaultRouteIndex's value
var serviceDomainList = [{
  domain: currentDomain,
  api2: API2_HOST,
  ws2: WS2_HOST
}, {
  domain: "".concat(currentDomain, "@1"),
  api2: getApi2HostsByServiceDomain(currentDomain)[0],
  ws2: getWs2HostsByServiceDomain(currentDomain)[0]
}, {
  domain: "".concat(currentDomain, "@2"),
  api2: getApi2HostsByServiceDomain(currentDomain)[1],
  ws2: getWs2HostsByServiceDomain(currentDomain)[1]
}, {
  domain: 'auto'
}];
var getWs2HostByServiceDomain = function getWs2HostByServiceDomain(item) {
  return "".concat(ws2Protocol, "//").concat(ws2Prefix).concat(item.trim());
};
var wsDomainList = [getWs2HostByServiceDomain(currentDomain)].concat(_toConsumableArray(BYBIT_SERVICE_DOMAINS.split(',').filter(Boolean).map(function (item) {
  return getWs2HostByServiceDomain(item);
})));

// Watched properties and default values
var defaultRouteIndex = 0; // default index is the current domain's config.
var watchedServiceHost = {
  API2_HOST: API2_HOST,
  WS2_HOST: WS2_HOST
};
var watchedAPI2Host = API2_HOST;
var watchedWS2Host = WS2_HOST;
var getRealDomain = function getRealDomain(newDomain) {
  var idx = serviceDomainList.findIndex(function (_ref) {
    var domain = _ref.domain;
    return domain === newDomain;
  });
  var idxExist = idx > -1;
  var defaultDomain = serviceDomainList[defaultRouteIndex];
  return {
    idx: idxExist ? idx : defaultRouteIndex,
    item: idxExist ? serviceDomainList[idx] : defaultDomain
  };
};
var domainChangeWatchers = [];
var prevDomain = '';

/**
 * Set the new domain
 *
 * @param {string} newDomain The new domain to update
 */
var setDomainConfig = function setDomainConfig(newDomain) {
  if (prevDomain && prevDomain === newDomain) return;
  prevDomain = newDomain;
  var _getRealDomain = getRealDomain(newDomain),
    item = _getRealDomain.item;
  watchedAPI2Host = item.api2;
  watchedWS2Host = item.ws2;
  domainChangeWatchers.forEach(function (cb) {
    return cb(item);
  });
};

// *********************
// Route detection task
// let routeDetectionTimer;
var detectInProgress = false;
var startRouteDetection = function startRouteDetection() {
  setInterval(function () {
    if (detectInProgress || document && document.hidden) return;
    detectInProgress = true;
    detectRoute(serviceDomainList).then(setDomainConfig).finally(function () {
      detectInProgress = false;
    });
  }, ROUTE_CHECK_DURATION);
};

// const stopRouteDetection = () => clearInterval(routeDetectionTimer);
// *********************

// *********************
// Init function
var routeChosenStorageKey = 'route_chosen_v2';
var selectedStorageDomain = storage.get(routeChosenStorageKey);
if (selectedStorageDomain === 'auto') {
  setDomainConfig(getCalculatedRoute());
  // startRouteDetection();
} else if (selectedStorageDomain && !selectedStorageDomain.includes(currentDomain)) {
  // This logic is to handle legacy local storage value.
  // If the stored domain does not match the current domain, remove the LS value and
  // use the default request route
  storage.remove(routeChosenStorageKey);
  setDomainConfig(null);
} else {
  setDomainConfig(selectedStorageDomain);
}
// *********************

// Watch and return correct host
Object.defineProperties(watchedServiceHost, {
  API2_HOST: {
    get: function get() {
      return XAPI_GRAY ? '/x-api' : watchedAPI2Host;
    }
  },
  WS2_HOST: {
    get: function get() {
      return watchedWS2Host;
    }
  }
});

// Instead of properties, using function to get correct domain
var getServiceHost = function getServiceHost() {
  return {
    API2_HOST: XAPI_GRAY ? '/x-api' : watchedAPI2Host,
    WS2_HOST: watchedWS2Host
  };
};

// User change route function
var changeRoute = function changeRoute(newDomain) {
  if (typeof window !== 'undefined' && window) {
    // just report the change to GA
    if (selectedStorageDomain && Array.isArray(window.dataLayer)) {
      window.dataLayer.push({
        event: 'GAEvent',
        eventAction: 'change-route',
        eventCategory: 'FE-Perf',
        eventLabel: "".concat(selectedStorageDomain, "->").concat(newDomain)
      });
    }
    if (newDomain !== 'auto') removeRecords();
    storage.set(routeChosenStorageKey, newDomain);
    window.location.reload();
  }
};

// Init select
var initRoute = selectedStorageDomain ? getRealDomain(selectedStorageDomain) : {
  idx: 3,
  item: serviceDomainList[3]
};
export { serviceDomainList, watchedServiceHost, domainChangeWatchers, getServiceHost, changeRoute, sendRequest, initRoute, getRealDomain, wsDomainList };